
import ErrorScreen from './components/ErrorScreen';
import React, { useEffect, ReactNode, Suspense, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { AuthShellHandoff } from './components/AuthShellHandoff';
import { db } from './services/db';
import { trackPageView } from './services/analytics';
import { setSentryUser, clearSentryUser, captureException as sentryCaptureException, captureMessage as sentryCaptureMessage } from './services/sentry';
import { lazyComponent } from './utils/lazyComponent';
import OptimizedLandingPage from './components/landing/OptimizedLandingPage';
import RouteSeoPolicy from './components/RouteSeoPolicy';
import AuthExpiryPolicy, { AUTH_PROTECTED_ROUTES } from './components/AuthExpiryPolicy';
import BlogArticleModuleFallback, { BlogArticleModuleBoundary } from './components/blog/BlogArticleModuleFallback';
import ThemeRoutePolicy from './components/ThemeRoutePolicy';
import { shouldUseRedesignForSurface, useUiReleaseMode } from './config/uiRelease';
import { isKnownHarmlessBrowserDomError } from './utils/errorClassifiers';

// Lazy-loaded auth and legal components to reduce initial bundle size
const Auth = React.lazy(() => import('./components/Auth'));
const Legal = React.lazy(() => import('./components/Legal'));
const VerifyEmail = React.lazy(() => import('./components/VerifyEmail'));
const WelcomeBack = React.lazy(() => import('./components/WelcomeBack'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const SSOHandler = React.lazy(() => import('./components/SSOHandler'));

// Lazy-loaded core components
// ⚡ Routed through lazyComponent() (not bare React.lazy) so a stalled chunk
// fetch rejects after a timeout instead of hanging forever — and emits a Sentry
// event. This is the safety net for reaching /dashboard or /admin via any path
// other than the login screen (direct URL, in-app nav), where Auth's login
// hand-off watchdog does not apply.
const Dashboard = lazyComponent(() => import('./components/Dashboard'), 'Dashboard');
const AdminPanel = lazyComponent(() => import('./components/admin/AdminPanel'), 'AdminPanel');
const HelpDesk = React.lazy(() => import('./components/helpdesk/HelpDesk'));
const Blog = React.lazy(() => import('./components/blog/Blog'));
const ChatWidget = React.lazy(() => import('./components/ChatWidget'));
const PublicChatReply = React.lazy(() => import('./components/PublicChatReply'));
const AboutPage = React.lazy(() => import('./components/AboutPage'));
const AccountDeletionPage = React.lazy(() => import('./components/AccountDeletionPage'));

// Homepage — keep the primary landing page in the initial app bundle so the
// first paint does not flash the generic Suspense loader before the hero.
const LandingPage = React.lazy(() => import('./components/landing/LandingPage'));
const MetadeckLanding = React.lazy(() => import('./components/landing/MetadeckLanding'));
const YouTubeSmmPreview = React.lazy(() => import('./components/youtube_smm/YouTubeSmmPreview'));
const YouTubeLanding = React.lazy(() => import('./components/landing/YouTubeLanding'));
// /youtube in the dot-matrix system (root DESIGN.md), same parts as the homepage; the old page stays on /youtube-preview.
const YouTubeLandingDotMatrix = React.lazy(() => import('./components/landing/YouTubeLandingDotMatrix'));
// /mcp-server: AI assistants (Claude, ChatGPT, Codex …) through the MCP server, dot-matrix system. /mcp itself is the server.
const McpLandingDotMatrix = React.lazy(() => import('./components/landing/McpLandingDotMatrix'));
const PricingPage = React.lazy(() => import('./components/PricingPage'));
const LandingRedesignPage = React.lazy(() => import('./components/landing/LandingRedesignPreview'));
const AdminRedesignPage = React.lazy(() => import('./components/admin/redesign/AdminRedesignPreview'));
// DEV-only: reseller page inside the dashboard shell without a login (components/reseller/ResellerShellPreview.tsx).
const ResellerShellPreview = React.lazy(() => import('./components/reseller/ResellerShellPreview'));
// The reseller's own admin, full screen in its own tab (components/reseller/admin/AdminApp.tsx).
const ResellerAdminApp = React.lazy(() => import('./components/reseller/admin/AdminApp'));
// Der V2-Entwurf ist am 09.08.2026 auf Wunsch entfernt worden (Aussehen).
// Gesichert auf dem Branch `archive/landing-v2-20260809` — dort liegen auch
// LandingCompare (Vorher/Nachher-Slider) und die Lieferprotokoll-Grafik.
// `LandingRedesignV2.css` ist absichtlich geblieben: LandingRedesignV3 importiert sie.
// V3: derselbe Inhalt und dasselbe Farbsystem wie V2, Praesentation mit React Bits
// (components/reactbits/). Ebenfalls nur lokal — zieht gsap/ogl nach und laedt
// deshalb bewusst lazy, damit nichts davon im Bundle der Live-Startseite landet.
const LandingRedesignV3Page = React.lazy(() => import('./components/landing/LandingRedesignV3'));
const LandingV4Page = React.lazy(() => import('./components/landing/LandingV4'));
const PublicSpecialReleaseSurface = React.lazy(() => import('./components/landing/PublicSpecialReleaseSurface'));
const ReleaseControlPage = import.meta.env.DEV
    ? React.lazy(() => import('./components/ReleaseControlPage'))
    : null;
// Template-Kit: Katalog der Sektionsbausteine unter /template. Nur lokal.
// Anleitung: frontend/template/TEMPLATE.md
//
// 🔴 Die Bedingung gehoert um den `React.lazy`-Aufruf, nicht nur um die Route —
// genau wie oben bei `ReleaseControlPage`. Ein `React.lazy` auf oberster Ebene
// ist ein erreichbarer dynamischer Import, und Rollup baut den Chunk dann auch
// dann, wenn die Route im Produktionsbuild toter Code ist. Am 2026-08-09 genau
// so gemessen: `TemplateGallery-*.js` lag im `dist/`, obwohl `/template` dort
// nicht existiert — und mit ihm der Beams-Hero samt three.js.
const TemplateGalleryPage = import.meta.env.DEV
    ? React.lazy(() => import('./template/gallery/TemplateGallery'))
    : null;
import { brandConfig } from './config/brand';
import BrandLoader from './components/loader/BrandLoader';

// Dynamic landing page loader — replaces 1,050+ explicit Route definitions
// The route map and individual components are loaded on-demand
const LandingPageLoader = React.lazy(() => import('./routes/LandingPageLoader'));
const HomepageSurface = React.lazy(() => import('./routes/HomepageSurface'));

// Every full-screen loading state (app and route Suspense fallbacks) is the one loading
// screen from components/loader/: the same scene as the boot loader in index.html and on
// its clock, so boot loader -> fallback -> page is one continuous shot and no dark or
// differently branded frame flashes in between.

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/* Der Absturzbildschirm ist fuer viele Kunden die letzte Seite, die sie sehen -
   von dort muss ein Support-Weg abgehen, der nicht wieder durch die App fuehrt.
   Der Link traegt Fehlertext, Route und (falls lesbar) die Kontomail schon im
   Nachrichtentext, damit der Support nicht erst danach fragen muss. */
const buildErrorSupportWhatsAppUrl = (error: Error | null): string => {
    const number = brandConfig.supportWhatsAppNumber || '';
    const parts = ['Hi! I hit an error in Traffic Creator and need help.'];
    try {
        parts.push(`Page: ${window.location.pathname}`);
    } catch {
        // location darf den Support-Link nie verhindern.
    }
    const message = error?.message ? String(error.message).slice(0, 200) : '';
    if (message) parts.push(`Error: ${message}`);
    try {
        const user = db.getCurrentUser();
        if (user?.email) parts.push(`Account: ${user.email}`);
    } catch {
        // Kein Zugriff auf die Sitzung: Link geht trotzdem raus.
    }
    return `https://wa.me/${number}?text=${encodeURIComponent(parts.join('\n'))}`;
};

const DOM_RACE_REPORT_KEY = 'tc_dom_race_reported';

/* Die DOM-Rennen selbst bleiben in `ignoreErrors` — sie sind harmlos und kaemen
   massenhaft. Gemeldet wird stattdessen EINE Zaehlmeldung je Sitzung mit einem
   Namen, der durch keinen Filter faellt. Ohne sie ist nicht messbar, wie viele
   Kunden das trifft; genau daran ist der Fall vom 2026-08-22 tagelang
   vorbeigelaufen (Tickets: 0 Treffer, Logs: 0 Treffer, Realitaet: mindestens
   ein Kunde, der die Kampagnenanlage nicht durchlaufen konnte). */
const reportDomRaceRecovered = (error: unknown, occurrence: number): void => {
    try {
        if (sessionStorage.getItem(DOM_RACE_REPORT_KEY) === '1') return;
        sessionStorage.setItem(DOM_RACE_REPORT_KEY, '1');
    } catch {
        // Ohne Storage lieber einmal zu viel melden als gar nicht.
    }

    const message = error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: unknown }).message ?? '')
        : String(error ?? '');

    // Chrome haengt der Wurzel `translated-ltr`/`translated-rtl` an, sobald die
    // Seitenuebersetzung laeuft — damit ist im Nachhinein belegbar, ob der
    // Uebersetzer im Spiel war, statt es nur zu vermuten.
    let translated = 'unknown';
    try {
        const cls = document.documentElement.className || '';
        translated = /\btranslated-(ltr|rtl)\b/.test(cls) ? 'yes' : 'no';
    } catch {
        // Kein DOM-Zugriff: bleibt 'unknown'.
    }

    sentryCaptureMessage('dom-race recovered by error boundary', 'warning', {
        tags: {
            alert_scope: 'dom_race_recovered',
            dom_race_kind: message.includes('insertBefore') ? 'insert'
                : message.includes('removeChild') ? 'remove'
                : 'other',
            page_translated: translated,
        },
        extra: {
            // Der Originaltext gehoert in `extra`, nicht in die Meldung: die
            // Filterliste prueft Meldung und Exception, nicht die Zusatzfelder.
            original_message: message.slice(0, 300),
            path: typeof window === 'undefined' ? '' : window.location.pathname,
            occurrence,
            html_lang: (() => {
                try { return document.documentElement.lang || ''; } catch { return ''; }
            })(),
        },
    });
};

const DOM_RECOVERY_RELOAD_KEY = 'tc_dom_recovery_reload';
const DOM_RECOVERY_RELOAD_THRESHOLD = 3;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    props: ErrorBoundaryProps;
    state: ErrorBoundaryState = { hasError: false, error: null };
    _domRecoveryCount = 0;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.props = props;
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');
            
        // Don't render the error boundary UI for chunk load errors, let componentDidCatch reload it
        if (isChunkLoadError || isKnownHarmlessBrowserDomError(error)) {
            return { hasError: false, error: null };
        }
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');

        if (isKnownHarmlessBrowserDomError(error)) {
            /* Swallowing lets React re-commit the tree. If the DOM keeps being
               rewritten under us (translator extensions do this continuously),
               re-committing never converges, so fall back to exactly one
               cache-distinct reload per session instead of spinning. */
            this._domRecoveryCount = (this._domRecoveryCount || 0) + 1;
            reportDomRaceRecovered(error, this._domRecoveryCount);
            if (this._domRecoveryCount > DOM_RECOVERY_RELOAD_THRESHOLD) {
                try {
                    if (sessionStorage.getItem(DOM_RECOVERY_RELOAD_KEY) === '1') return;
                    sessionStorage.setItem(DOM_RECOVERY_RELOAD_KEY, '1');
                } catch {
                    // Storage can be blocked; the single reload below still helps.
                }
                const recoveryUrl = new URL(window.location.href);
                recoveryUrl.searchParams.set('tc_dom_recovery', Date.now().toString());
                window.location.replace(recoveryUrl.toString());
            }
            return;
        }

        if (isChunkLoadError) {
            // Prevent infinite reload loops 
            const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
            if (reloadCount < 2) {
                sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                console.warn('Chunk load error detected, bypassing cache to fetch latest version...');
                // Bust cache by appending a unique timestamp to the URL query string
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.set('v', Date.now().toString());
                window.location.replace(newUrl.toString());
                return;
            }
        }

        // Report to Sentry if not a simple chunk reload issue
        sentryCaptureException(error, {
            context: { source: 'ErrorBoundary' },
            extra: { componentStack: errorInfo.componentStack }
        });
    }

    componentDidMount() {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const message = reason?.message || (typeof reason === 'string' ? reason : '');
            
            const isChunkLoadError = 
                reason?.name === 'ChunkLoadError' ||
                message.includes('Failed to fetch dynamically imported module') ||
                message.includes('dynamically imported module') ||
                message.includes('Loading chunk');

            if (isChunkLoadError) {
                const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
                if (reloadCount < 2) {
                    sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                    console.warn('Unhandled chunk load error detected, bypassing cache...');
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('v', Date.now().toString());
                    window.location.replace(newUrl.toString());
                    return;
                }
            }

            sentryCaptureException(event.reason, { context: { source: 'ErrorBoundary' } });
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        (this as any)._unhandledRejectionHandler = handleUnhandledRejection;
    }

    componentWillUnmount() {
        if ((this as any)._unhandledRejectionHandler) {
            window.removeEventListener('unhandledrejection', (this as any)._unhandledRejectionHandler);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <ErrorScreen
                    error={this.state.error}
                    supportUrl={brandConfig.supportWhatsAppNumber ? buildErrorSupportWhatsAppUrl(this.state.error) : null}
                />
            );
        }

        return this.props.children;
    }
}

/** Captures /ref/:code and /ref/u/:code → redirects to /signup?ref=:code */
const ReferralRedirect: React.FC = () => {
    const { code } = useParams();
    return <Navigate to={`/signup?ref=${code || ''}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ children, adminOnly }) => {
    const user = db.getCurrentUser();
    const location = useLocation();

    if (!user) {
        // Come back here after signing in (Auth.tsx checks it with getRoleSafeReturnPath). Needed for the OAuth
        // consent page /dashboard/connect/<id>, which an AI app opens before the customer has signed in.
        const back = `${location.pathname}${location.search}`;
        return <Navigate to={back === '/dashboard' ? '/login' : `/login?return=${encodeURIComponent(back)}`} replace />;
    }

    if (adminOnly && user.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

const LegalWrapper: React.FC = () => {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();

    const handleBack = () => {
        const user = db.getCurrentUser();
        if (user && (user.role === 'user' || user.role === 'admin')) {
            navigate('/dashboard');
        } else {
            navigate('/');
        }
    };

    return <Legal type={type as any} onBack={handleBack} />;
};

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const uiReleaseMode = useUiReleaseMode();
    const [chatWidgetReady, setChatWidgetReady] = useState(false);
    const chatWidgetLoadedRef = useRef(false);
    const isLocalDesignPreview = import.meta.env.DEV
        && (location.pathname === '/landing-redesign-preview'
            || location.pathname.startsWith('/admin-redesign-preview')
            || location.pathname.startsWith('/reseller-preview')
            || location.pathname.startsWith('/whitelabel-preview')
            || location.pathname === '/release-control');

    useEffect(() => {
        document.documentElement.dataset.uiRelease = uiReleaseMode;
        if (window.gtag) {
            window.gtag('set', { ui_release: uiReleaseMode });
        }
    }, [uiReleaseMode]);

    // Initialize DB on mount
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        db.init();

        // Capture Google Ads Click ID (GCLID) for Value-Based Bidding
        const urlParams = new URLSearchParams(window.location.search);
        const clickId = urlParams.get('gclid');
        if (clickId) {
            localStorage.setItem('tgp_gclid', clickId);
        }
    }, [isLocalDesignPreview]);

    // Sync Sentry user context with auth state
    useEffect(() => {
        if (isLocalDesignPreview) {
            clearSentryUser();
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            setSentryUser({ id: user.id, email: user.email, role: user.role });
        } else {
            clearSentryUser();
        }
    }, [location, isLocalDesignPreview]);

    // Track Presence based on location
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            // Map path to "view" for legacy support in presence tracking
            const path = location.pathname.substring(1) || 'landing';
            db.trackPresence(path, user);

            // Set GA4 User-ID for cross-session/cross-device tracking
            if (window.gtag) {
                window.gtag('set', { user_id: user.id });
            }
        }
    }, [location, isLocalDesignPreview]);

    // Track SPA page views in GA4 on every route change
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }
        trackPageView(location.pathname + location.search);
    }, [location, isLocalDesignPreview]);


    // Keep route ownership with React Router. Calling navigate() from a global
    // Navigation API interceptor starts a second navigation inside the first in
    // Chromium, which skips the active View Transition and emits AbortError
    // noise. The routed Suspense fallbacks already provide continuous feedback.
    const navigateToRoot = () => {
        navigate('/');
        window.scrollTo(0, 0);
    };

    // ── Chunk-Reload-Counter reset ──
    // The ErrorBoundary bumps sessionStorage['chunk_reload_count'] on every
    // ChunkLoadError and gives up after 2 reloads. Without a reset, a user who
    // hits 2 chunk failures early in a session gets dead clicks forever
    // (subsequent lazy loads silently fail). Reset on every successful route
    // change so the counter only tracks *consecutive* failures.
    useEffect(() => {
        sessionStorage.removeItem('chunk_reload_count');
    }, [location.pathname]);

    // Keep public landing pages focused on the hero first. The lightweight
    // WhatsApp launcher is deferred until idle, while dashboard/support pages
    // keep it immediately available.
    useEffect(() => {
        if ((window as any).__PRERENDER__) {
            setChatWidgetReady(false);
            return;
        }

        if (isLocalDesignPreview) {
            setChatWidgetReady(false);
            return;
        }

        const loadImmediately =
            location.pathname.startsWith('/dashboard') ||
            location.pathname.startsWith('/admin') ||
            location.pathname.startsWith('/support/live');

        if (loadImmediately) {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
            return;
        }

        if (chatWidgetLoadedRef.current) {
            setChatWidgetReady(true);
            return;
        }

        setChatWidgetReady(false);
        let idleId: number | null = null;
        const showChatWidget = () => {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
        };
        const timeoutId = window.setTimeout(() => {
            if ('requestIdleCallback' in window) {
                idleId = window.requestIdleCallback(showChatWidget, { timeout: 2500 });
            } else {
                showChatWidget();
            }
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
            if (idleId !== null && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
        };
    }, [location.pathname, isLocalDesignPreview]);

    const handleLogin = (role: 'user' | 'admin') => {
        // ⚠️ Plain navigate(): the destination
        // (Dashboard/AdminPanel) is React.lazy, so its render suspends while the
        // chunk loads. document.startViewTransition() snapshots the current page
        // (Auth's white success screen) and freezes it as an overlay until the
        // callback's DOM update settles — but navigate() returns before the lazy
        // chunk resolves, so the frozen success-screen screenshot can stay on top
        // indefinitely and the user never reaches the dashboard. The Suspense
        // fallback (branded loader) handles the chunk-load wait correctly instead.
        navigate(role === 'admin' ? '/admin' : '/dashboard');
        window.scrollTo(0, 0);
    };

    const handleLogout = () => {
        // Clear GA4 User-ID on logout
        if (window.gtag) {
            window.gtag('set', { user_id: null });
        }
        db.logout();
    };

    const handleBack = () => {
        navigateToRoot();
    };

    return (
        <ErrorBoundary>
            <AuthExpiryPolicy disabled={isLocalDesignPreview} />
            <RouteSeoPolicy />
            <ThemeRoutePolicy />
            <Suspense fallback={<BlogArticleModuleFallback><BrandLoader /></BlogArticleModuleFallback>}>
                <Routes>
                    <Route path="/" element={
                        brandConfig.id === 'trafficcreator'
                        ? <HomepageSurface
                            uiReleaseMode={uiReleaseMode}
                            onLogin={() => navigate('/login')}
                            onNavigate={(page) => navigate(`/${page}`)}
                          />
                        : <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                    } />

                    {/* Design previews are local-only and never ship as public bypass routes. */}
                    {import.meta.env.DEV && (
                        <>
                            <Route path="/metadeck-preview" element={
                                <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            <Route path="/landing-new" element={
                                <OptimizedLandingPage onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            {brandConfig.id === 'trafficcreator' && (
                                <>
                                    <Route path="/landing-redesign-preview" element={<LandingRedesignPage previewMode />} />
                                    <Route path="/landing-v3" element={<LandingRedesignV3Page />} />
                                    <Route path="/landing-v4" element={<LandingV4Page />} />
                                    {TemplateGalleryPage && (
                                        <>
                                            <Route path="/template" element={<TemplateGalleryPage />} />
                                            {/* Einzelne Variante bildschirmfuellend — so laesst sich das
                                                Verhalten bei anderen Breiten am echten Viewport pruefen. */}
                                            <Route path="/template/:variantId" element={<TemplateGalleryPage />} />
                                        </>
                                    )}
                                </>
                            )}
                            {/* Splat: AdminPanel mounts its own nested <Routes> for every admin page. */}
                            <Route path="/admin-redesign-preview/*" element={<AdminRedesignPage />} />
                            <Route path="/youtube-smm-preview" element={<YouTubeSmmPreview />} />
                            <Route path="/error-preview" element={<ErrorScreen error={new TypeError("Cannot access 'searchPct' before initialization")} supportUrl={brandConfig.supportWhatsAppNumber ? buildErrorSupportWhatsAppUrl(new Error('Preview')) : null} />} />
                            <Route path="/reseller-preview/*" element={<ResellerShellPreview />} />
                            <Route path="/whitelabel-preview/admin/*" element={<ResellerAdminApp />} />
                            <Route path="/youtube-preview" element={
                                <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                        </>
                    )}

                    {ReleaseControlPage && (
                        <Route path="/release-control" element={<ReleaseControlPage />} />
                    )}

                    <Route path="/youtube" element={
                        brandConfig.id === 'trafficcreator' && shouldUseRedesignForSurface(uiReleaseMode, 'youtube') ? (
                            <YouTubeLandingDotMatrix />
                        ) : (
                            <PublicSpecialReleaseSurface mode={brandConfig.id === 'trafficcreator' ? uiReleaseMode : 'legacy'} surface="youtube">
                                <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            </PublicSpecialReleaseSurface>
                        )
                    } />

                    {brandConfig.id === 'trafficcreator' && (
                        <Route path="/mcp-server" element={<McpLandingDotMatrix />} />
                    )}

                    {brandConfig.id === 'trafficcreator' && (
                        <Route path="/pricing" element={
                            <PublicSpecialReleaseSurface mode={uiReleaseMode} surface="pricing">
                                <PricingPage />
                            </PublicSpecialReleaseSurface>
                        } />
                    )}

                    {/* Auth Routes */}
                    <Route path="/login" element={<AuthShellHandoff><Auth view="login" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} /></AuthShellHandoff>} />
                    <Route path="/signup" element={<AuthShellHandoff><Auth view="signup" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} /></AuthShellHandoff>} />
                    <Route path="/forgot" element={<AuthShellHandoff><Auth view="forgot" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} /></AuthShellHandoff>} />
                    <Route path="/verify-email" element={<AuthShellHandoff><VerifyEmail /></AuthShellHandoff>} />
                    <Route path="/welcome-back" element={<WelcomeBack />} />
                    <Route path="/reset-password" element={<AuthShellHandoff><ResetPassword /></AuthShellHandoff>} />
                    <Route path="/sso" element={<SSOHandler />} />

                    {/* Referral link redirects → signup with ref code */}
                    <Route path="/ref/:code" element={<ReferralRedirect />} />
                    <Route path="/ref/u/:code" element={<ReferralRedirect />} />
                    <Route path="/register" element={<Navigate to={`/signup${window.location.search}`} replace />} />
                    <Route path="/support/live/:ticketId" element={<PublicChatReply />} />


                    {/* Protected Routes — wrapped in their own Suspense so the lazy
                        Dashboard/Admin chunk waits inside the route (ProtectedRoute
                        keeps rendering) on the same loading screen. */}
                    <Route path={AUTH_PROTECTED_ROUTES.dashboard} element={
                        <ProtectedRoute>
                            <Suspense fallback={<BrandLoader />}>
                                <Dashboard onLogout={handleLogout} onNavigate={(page) => navigate(`/${page}`)} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path="/whitelabel/admin/*" element={
                        <ProtectedRoute>
                            <Suspense fallback={<BrandLoader />}>
                                <ResellerAdminApp />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path={AUTH_PROTECTED_ROUTES.admin} element={
                        <ProtectedRoute adminOnly>
                            <Suspense fallback={<BrandLoader />}>
                                <AdminPanel onLogout={handleLogout} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    {/* Other Pages */}
                    {brandConfig.id === 'trafficcreator' && (
                        <>
                            <Route path="/helpdesk" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category/:slug" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/blog/:slug" element={<BlogArticleModuleBoundary><Blog onBack={handleBack} /></BlogArticleModuleBoundary>} />
                            <Route path="/:lang/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/:lang/blog/:slug" element={<BlogArticleModuleBoundary><Blog onBack={handleBack} /></BlogArticleModuleBoundary>} />
                            
                            {/* Legal Routes */}
                            <Route path="/legal/:type" element={<LegalWrapper />} />
                            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                            <Route path="/impressum" element={<Navigate to="/legal/impressum" replace />} />
                            <Route path="/agb" element={<Navigate to="/legal/agb" replace />} />
                            <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
                            <Route path="/delivery" element={<Navigate to="/legal/delivery" replace />} />
                            <Route path="/account-deletion" element={<AccountDeletionPage />} />

                            {/* About Page */}
                            <Route path="/about" element={<AboutPage onBack={handleBack} />} />

                            {import.meta.env.DEV && (
                                <Route path="/pricing-sample" element={
                                    <React.Suspense fallback={<BrandLoader />}>
                                        {React.createElement(React.lazy(() => import('./components/landing/SamplePricing')))}
                                    </React.Suspense>
                                } />
                            )}
                        </>
                    )}

                    {/* 
                     * Dynamic Landing Pages — replaces 1,050+ explicit route definitions.
                     * The LandingPageLoader dynamically imports the route map and resolves
                     * the correct landing page component based on the current URL pathname.
                     * This moves ~330KB of route declarations out of the main bundle.
                     */}
                    { brandConfig.id === 'trafficcreator' && <Route path="*" element={<LandingPageLoader />} /> }
                    { brandConfig.id === 'metadeck' && <Route path="*" element={<Navigate to="/" replace />} /> }
                </Routes>
            </Suspense>
            {chatWidgetReady && !isLocalDesignPreview && !(window as any).__TC_WL_DEMO__ && (
                <Suspense fallback={null}>
                    <ChatWidget />
                </Suspense>
            )}
        </ErrorBoundary>
    );
};

export default App;
